import React from "react";
import "./ProgressToTarget.css";

export type ProgressToTargetProps = {
  /** Name of the active target (e.g. "FY2026 OHT attachment target"). Shown as context beneath the generic heading. */
  targetName: string;
  /** Plain-English time window/context currently being viewed on the dashboard. */
  timeRangeLabel: string;
  /** Target goal (e.g. annual patient attachment goal). */
  goal: number;
  /** Number of attachments completed so far for this target. */
  attached: number;
  /** Linear expected count at today's point in the period. */
  expectedToday: number;
  /**
   * Days remaining to reach the target at the current pace.
   * Pass null when indeterminate.
   */
  daysToTarget: number | null;
  /**
   * Daily pace used for ETA.
   */
  pacePerDay: number | null;
  /**
   * Current remaining declared capacity across the OHT.
   * Optional so older callers do not break; pass this from Overview for the full bar.
   */
  remainingCapacity?: number | null;
  /**
   * Optional status label shown as a pill in the section header.
   * Derived by caller based on actual vs expected.
   */
  status?: "on-track" | "at-risk" | "behind" | "complete";
};

type Segment = {
  key: string;
  label: string;
  value: number;
  pct: number;
  color: string;
  textColor: string;
  minVisiblePct: number;
};

function clampPercent(n: number): number {
  if (!Number.isFinite(n)) return 0;
  return Math.max(0, Math.min(100, n));
}

function safeNumber(value: number | null | undefined): number {
  if (value === null || value === undefined || Number.isNaN(Number(value))) {
    return 0;
  }

  return Math.max(0, Number(value));
}

function fmtNumber(value: number | null | undefined): string {
  if (value === null || value === undefined || Number.isNaN(Number(value))) {
    return "—";
  }

  return Number(value).toLocaleString();
}

function fmtDays(value: number | null | undefined): string {
  if (value === null || value === undefined || Number.isNaN(Number(value))) {
    return "—";
  }

  if (Number(value) <= 0) return "Complete";
  if (Math.ceil(Number(value)) === 1) return "1 day";
  return `${Math.ceil(Number(value)).toLocaleString()} days`;
}

function fmtPace(value: number | null | undefined): string {
  if (value === null || value === undefined || Number.isNaN(Number(value))) {
    return "—";
  }

  return `${Number(value).toFixed(1)} patients / day`;
}

function statusMeta(status: ProgressToTargetProps["status"]) {
  switch (status) {
    case "complete":
      return { label: "Complete", toneClass: "oht-ptt-status--good" };
    case "on-track":
      return { label: "On track", toneClass: "oht-ptt-status--good" };
    case "at-risk":
      return { label: "At risk", toneClass: "oht-ptt-status--warn" };
    case "behind":
      return { label: "Behind", toneClass: "oht-ptt-status--danger" };
    default:
      return { label: "", toneClass: "" };
  }
}

function buildWholeOhtInsight({
  goal,
  attached,
  expectedToday,
  remainingCapacity,
  capacitySegment,
  capacityShortfall,
  remainingToTarget,
  daysToTarget,
  pacePerDay,
}: {
  goal: number;
  attached: number;
  expectedToday: number;
  remainingCapacity: number | null;
  capacitySegment: number;
  capacityShortfall: number | null;
  remainingToTarget: number;
  daysToTarget: number | null;
  pacePerDay: number | null;
}): string {
  if (goal <= 0) {
    return "No active whole-OHT target value is available. Once a valid target is set, this section will show whether current attachments and remaining capacity are enough to meet it.";
  }

  if (attached >= goal) {
    if (remainingCapacity !== null && remainingCapacity > 0) {
      return `The whole-OHT target appears to be complete, with ${fmtNumber(
        attached
      )} attachment(s) against a target of ${fmtNumber(
        goal
      )}. There are still ${fmtNumber(
        remainingCapacity
      )} declared space(s) available, which may support future planning or overflow if appropriate.`;
    }

    return `The whole-OHT target appears to be complete, with ${fmtNumber(
      attached
    )} attachment(s) against a target of ${fmtNumber(
      goal
    )}. Continue monitoring capacity updates and new pipeline pressure.`;
  }

  if (remainingCapacity === null) {
    const paceText =
      pacePerDay !== null && pacePerDay > 0
        ? ` At the current pace of ${fmtPace(
            pacePerDay
          )}, the estimated time to close the target gap is ${fmtDays(
            daysToTarget
          )}.`
        : " Current pace is unavailable, so timeline risk cannot be assessed.";

    return `The OHT has ${fmtNumber(
      remainingToTarget
    )} patient(s) remaining to reach the active target. Remaining capacity was not provided to this component, so the page can show target progress but cannot assess whether declared capacity is enough to close the gap.${paceText}`;
  }

  if (capacityShortfall !== null && capacityShortfall > 0) {
    return `The OHT has a capacity shortfall against the active target. There are ${fmtNumber(
      remainingToTarget
    )} patient(s) left to attach, but only ${fmtNumber(
      remainingCapacity
    )} declared space(s) available. Even if all current capacity is used, the OHT is short by ${fmtNumber(
      capacityShortfall
    )} space(s). Additional capacity, provider confirmation, or routing to surplus areas may be needed.`;
  }

  if (pacePerDay === null || pacePerDay <= 0) {
    return `Current remaining capacity appears sufficient to cover the target gap, with ${fmtNumber(
      capacitySegment
    )} available space(s) contributing toward the remaining target. However, there is no current attachment pace, so the OHT may need to review whether requests are being routed, accepted, and completed.`;
  }

  const behindBy = Math.max(0, Math.round(expectedToday - attached));
  const aheadBy = Math.max(0, Math.round(attached - expectedToday));

  if (behindBy > 0) {
    return `Current remaining capacity appears sufficient to meet the target, but progress is behind the expected pace by ${fmtNumber(
      behindBy
    )} patient(s). At the current pace of ${fmtPace(
      pacePerDay
    )}, the estimated time to close the target gap is ${fmtDays(
      daysToTarget
    )}. The limiting factor may be workflow speed, routing volume, clinic response, or attachment completion rather than declared capacity.`;
  }

  if (aheadBy > 0) {
    return `The OHT is ahead of the expected progress line by ${fmtNumber(
      aheadBy
    )} patient(s), and current remaining capacity appears sufficient to close the target gap. At the current pace of ${fmtPace(
      pacePerDay
    )}, the estimated time to target is ${fmtDays(
      daysToTarget
    )}. Continue routing suitable patients while monitoring capacity updates.`;
  }

  return `Current remaining capacity appears sufficient to meet the whole-OHT target, and progress is aligned with the expected pace. At the current pace of ${fmtPace(
    pacePerDay
  )}, the estimated time to close the target gap is ${fmtDays(
    daysToTarget
  )}. Continue monitoring provider capacity and attachment completion.`;
}

/**
 * Compact whole-OHT target progress widget for the Overview page.
 *
 * The bar compares the active target with:
 *   - achieved attachments,
 *   - remaining capacity that can cover the target gap,
 *   - any capacity shortfall beyond declared capacity.
 *
 * The plain-language insight below the bar explains the operational meaning.
 */
export function ProgressToTarget({
  targetName,
  timeRangeLabel,
  goal,
  attached,
  expectedToday,
  daysToTarget,
  pacePerDay,
  remainingCapacity = null,
  status,
}: ProgressToTargetProps) {
  const safeGoal = safeNumber(goal);
  const safeAttached = safeNumber(attached);
  const safeExpectedToday = safeNumber(expectedToday);
  const normalizedRemainingCapacity =
    remainingCapacity === null || remainingCapacity === undefined
      ? null
      : safeNumber(remainingCapacity);

  const remainingToTarget = Math.max(safeGoal - safeAttached, 0);
  const achievedForBar = Math.min(safeAttached, safeGoal);

  const capacitySegment =
    normalizedRemainingCapacity === null
      ? 0
      : Math.min(normalizedRemainingCapacity, remainingToTarget);

  const capacityShortfall =
    normalizedRemainingCapacity === null
      ? null
      : Math.max(remainingToTarget - normalizedRemainingCapacity, 0);

  const achievedPct =
    safeGoal > 0 ? clampPercent((achievedForBar / safeGoal) * 100) : 0;

  const capacityPct =
    safeGoal > 0 ? clampPercent((capacitySegment / safeGoal) * 100) : 0;

  const shortfallPct =
    safeGoal > 0 && capacityShortfall !== null
      ? clampPercent((capacityShortfall / safeGoal) * 100)
      : 0;

  const targetComplete = safeGoal > 0 && safeAttached >= safeGoal;
  const meta = statusMeta(targetComplete ? "complete" : status);

  const segments: Segment[] = [
    {
      key: "attached",
      label: "Attached",
      value: achievedForBar,
      pct: achievedPct,
      color: "#16a34a",
      textColor: "#ffffff",
      minVisiblePct: 7,
    },
    {
      key: "capacity",
      label: "Capacity",
      value: capacitySegment,
      pct: capacityPct,
      color: "#1669b3",
      textColor: "#ffffff",
      minVisiblePct: 8,
    },
    {
      key: "shortfall",
      label: "Shortfall",
      value: capacityShortfall || 0,
      pct: shortfallPct,
      color: "#dc2626",
      textColor: "#ffffff",
      minVisiblePct: 8,
    },
  ].filter((segment) => segment.value > 0 && segment.pct > 0);

  const insight = buildWholeOhtInsight({
    goal: safeGoal,
    attached: safeAttached,
    expectedToday: safeExpectedToday,
    remainingCapacity: normalizedRemainingCapacity,
    capacitySegment,
    capacityShortfall,
    remainingToTarget,
    daysToTarget,
    pacePerDay,
  });

  return (
    <section className="ocf-card oht-ptt">
      <header className="oht-ptt-head">
        <div className="oht-ptt-title-block">
          <h2 className="ocf-section-title">Whole OHT target progress</h2>
          <div className="oht-ptt-subhead">
            <span className="oht-ptt-target-name">{targetName}</span>
            <span className="oht-ptt-sep">·</span>
            <span className="oht-ptt-range">
              <strong>{timeRangeLabel}</strong>
            </span>
          </div>
        </div>
        {meta.label ? (
          <span className={`oht-ptt-status ${meta.toneClass}`}>{meta.label}</span>
        ) : null}
      </header>

      <div
        style={{
          display: "grid",
          gap: 8,
          marginTop: 10,
        }}
      >
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            gap: 12,
            color: "#334e68",
            fontSize: 13,
            fontWeight: 800,
            flexWrap: "wrap",
          }}
        >
          <span>0</span>
          <span>Target {fmtNumber(safeGoal)}</span>
        </div>

        <div
          aria-label={`Whole OHT target progress: ${fmtNumber(
            safeAttached
          )} attached, ${fmtNumber(
            normalizedRemainingCapacity
          )} remaining capacity, target ${fmtNumber(safeGoal)}.`}
          style={{
            display: "flex",
            width: "100%",
            minHeight: 42,
            borderRadius: 14,
            overflow: "hidden",
            background: "#e7eef6",
            border: "1px solid rgba(15, 80, 135, 0.10)",
            boxShadow: "inset 0 1px 2px rgba(15, 23, 42, 0.08)",
          }}
        >
          {segments.length === 0 ? (
            <div
              style={{
                width: "100%",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                color: "#64748b",
                fontSize: 13,
                fontWeight: 800,
              }}
            >
              No progress data available
            </div>
          ) : (
            segments.map((segment) => {
              const showInsideLabel = segment.pct >= segment.minVisiblePct;

              return (
                <div
                  key={segment.key}
                  style={{
                    width: `${segment.pct}%`,
                    minWidth: showInsideLabel ? 88 : 10,
                    background: segment.color,
                    color: segment.textColor,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    padding: "0 8px",
                    fontSize: 12,
                    fontWeight: 900,
                    lineHeight: 1.2,
                    textAlign: "center",
                    whiteSpace: "nowrap",
                  }}
                  title={`${segment.label}: ${fmtNumber(segment.value)}`}
                >
                  {showInsideLabel ? (
                    <span>
                      {fmtNumber(segment.value)} {segment.label.toLowerCase()}
                    </span>
                  ) : null}
                </div>
              );
            })
          )}
        </div>

        <div
          style={{
            display: "flex",
            flexWrap: "wrap",
            gap: 10,
            color: "#475569",
            fontSize: 12,
            fontWeight: 800,
          }}
        >
          <span>
            <i
              style={{
                display: "inline-block",
                width: 9,
                height: 9,
                borderRadius: 999,
                background: "#16a34a",
                marginRight: 5,
              }}
            />
            Achieved {fmtNumber(safeAttached)}
          </span>
          <span>
            <i
              style={{
                display: "inline-block",
                width: 9,
                height: 9,
                borderRadius: 999,
                background: "#1669b3",
                marginRight: 5,
              }}
            />
            Remaining capacity {fmtNumber(normalizedRemainingCapacity)}
          </span>
          <span>
            <i
              style={{
                display: "inline-block",
                width: 9,
                height: 9,
                borderRadius: 999,
                background: "#dc2626",
                marginRight: 5,
              }}
            />
            Shortfall {fmtNumber(capacityShortfall)}
          </span>
          <span>ETA {fmtDays(daysToTarget)}</span>
        </div>
      </div>

      <div
        className={`oht-fsa-map-insight oht-fsa-map-insight--${
          capacityShortfall !== null && capacityShortfall > 0
            ? "danger"
            : targetComplete
            ? "good"
            : status === "behind" || status === "at-risk"
            ? "warn"
            : "good"
        }`}
        style={{ marginTop: 14 }}
      >
        <p style={{ margin: 0 }}>{insight}</p>
      </div>
    </section>
  );
}